import { useCallback } from 'react'

import { HoverEffectVariant, HoverIntensity, Quantity } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos, CoreComponentTableData } from '@/core/models/app-components/types'
import { useCompContext } from '@/context/__project__/CompContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { collectTableIds } from '@/shared/collect-table-ids'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

interface CompQuantityWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  tableData: CoreComponentTableData
}

export function CompQuantityWrapperCore({
  infos: { settings, detailTableId, detailRowIndex, containerClient, component },
  action: { simulateAction },
  tableData: { tableId: tableDataId, dbClient },
}: CompQuantityWrapperCoreProps) {
  const compId = component._id

  const { userSpecific, language, tablesMap } = useDbClientContext()
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()
  const compContext = useCompContext()

  // ========================= data =========================
  const colValue = settings[ComponentSettingKey.QuantityDataValue]

  const value = useParseVariableSelect(colValue, containerClient, detailTableId, detailRowIndex)

  const tableIds = Array.from(collectTableIds(colValue, dbClient).keys())
  const table = tablesMap.get(detailTableId || tableIds[0])

  const reverseMap = new Map()
  for (const [key, value] of table?.GetRowIndexesMap(language, userSpecific)?.entries() ?? []) {
    reverseMap.set(value, key)
  }

  const rowId = compContext.rowId || (colValue?.row ? reverseMap.get(Number(colValue?.row) - 1) : null) || ''
  const tableId = detailTableId || tableDataId || colValue?.tbl

  const min = settings[ComponentSettingKey.QuantityDataMin]
  const max = settings[ComponentSettingKey.QuantityDataMax]

  // ========================= design =========================
  const buttonColor = settings[ComponentSettingKey.QuantityDesignButtonColor]
  const buttonColorSolid = settings[ComponentSettingKey.QuantityDesignButtonColorSolid]
  const buttonColorStartGradient = settings[ComponentSettingKey.QuantityDesignButtonColorStartGradient]
  const buttonColorEndGradient = settings[ComponentSettingKey.QuantityDesignButtonColorEndGradient]
  const buttonColorGradientDirection = settings[ComponentSettingKey.QuantityDesignButtonColorGradientDirection]

  const inputColor = settings[ComponentSettingKey.QuantityDesignInputColor]
  const inputColorSolid = settings[ComponentSettingKey.QuantityDesignInputColorSolid]
  const inputColorStartGradient = settings[ComponentSettingKey.QuantityDesignInputColorStartGradient]
  const inputColorEndGradient = settings[ComponentSettingKey.QuantityDesignInputColorEndGradient]
  const inputColorGradientDirection = settings[ComponentSettingKey.QuantityDesignInputColorGradientDirection]

  const colLabel = settings[ComponentSettingKey.QuantityDataLabel]
  const label = useParseVariableText(colLabel, containerClient, detailTableId, detailRowIndex)

  const labelTypography = settings[ComponentSettingKey.QuantityDesignLabelTypography]

  const margin = settings[ComponentSettingKey.QuantityDesignMargin]

  const hoverEffect = settings[ComponentSettingKey.QuantityDesignHoverEffect] as HoverEffectVariant
  const hoverIntensity = 'md' as HoverIntensity

  // ========================= actions =========================
  const onIncrease = settings[ComponentSettingKey.QuantityActionOnIncrease]
  const onDecrease = settings[ComponentSettingKey.QuantityActionOnDecrease]
  const handleIncrease = async () => {
    await simulateAction({
      actions: onIncrease,
      tableId,
      rowId,
    })
  }

  const handleDecrease = async () => {
    await simulateAction({
      actions: onDecrease,
      tableId,
      rowId,
    })
  }

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  const toNumber = useCallback((str: string) => {
    const match = str?.match(/\d+/)
    return match ? Number(match[0]) : 0
  }, [])

  const quantityComponent = (
    <Quantity
      id={compId}
      value={toNumber(value as string) as number}
      onIncrease={handleIncrease}
      onDecrease={handleDecrease}
      min={min}
      max={max}
      buttonColorType={buttonColor}
      buttonColorSolid={buttonColorSolid}
      buttonColorStartGradient={buttonColorStartGradient}
      buttonColorEndGradient={buttonColorEndGradient}
      buttonColorGradientDirection={buttonColorGradientDirection}
      inputColorType={inputColor}
      inputColorSolid={inputColorSolid}
      inputColorStartGradient={inputColorStartGradient}
      inputColorEndGradient={inputColorEndGradient}
      inputColorGradientDirection={inputColorGradientDirection}
      label={label}
      showLabel={!!label}
      labelTypography={labelTypography}
      size='lg'
      margin={margin}
      hoverEffect={hoverEffect as HoverEffectVariant}
      hoverIntensity={hoverIntensity as HoverIntensity}
    />
  )

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {quantityComponent}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return quantityComponent
}
